import ErrorContainer from 'components/common/error-container';
import VerticalTabsMenu, { type VerticalTabItem } from 'components/common/vertical-tabs-menu';
import ProjectSettingsCredentials from 'components/projects/settings/credentials/project-credentials';
import ProjectSettingsGeneral from 'components/projects/settings/general/project-settings-general';
import ProjectSettingsUsers from 'components/projects/settings/users/project-settings-users';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import find from 'lodash/find';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ProjectTeamRoleAssignmentsCard from 'components/projects/settings/teams/project-team-role-assignments-card';
import ProjectSettingsPolicies from 'components/projects/settings/policies/project-settings-policies';
import ProjectSettingsNotifications from 'components/projects/settings/notifications/project-settings-notifications';
import type { Project } from 'types/api.types';
import { useCuratedProjects } from 'stores/rq/projects';
import useIsMobile from 'hooks/use-is-mobile';
import Loader from 'components/common/loader';
import {
  InfoCircleOutlined,
  UserOutlined,
  TeamOutlined,
  UnlockOutlined,
  SecurityScanOutlined,
  NotificationOutlined
} from '@ant-design/icons';

export const PROJECT_SETTINGS_MENU_KEYS = [
  'general',
  'users',
  'teams',
  'credentials',
  'policies',
  'notifications'
] as const;

type MenuKey = (typeof PROJECT_SETTINGS_MENU_KEYS)[number];

const MOBILE_PROJECT_SETTINGS_MENU_KEYS: MenuKey[] = ['general', 'policies'];

const menuComponents: Record<MenuKey, React.FunctionComponent<{ project: Project }>> = {
  general: ProjectSettingsGeneral,
  users: ProjectSettingsUsers,
  teams: ProjectTeamRoleAssignmentsCard,
  credentials: ProjectSettingsCredentials,
  policies: ProjectSettingsPolicies,
  notifications: ProjectSettingsNotifications
};

const getIconForKey = (key: MenuKey): React.ComponentType<any> => {
  const iconMap: Record<MenuKey, React.ComponentType<any>> = {
    general: InfoCircleOutlined,
    users: UserOutlined,
    teams: TeamOutlined,
    credentials: UnlockOutlined,
    policies: SecurityScanOutlined,
    notifications: NotificationOutlined
  };
  return iconMap[key];
};

const allProjectMenuItems: VerticalTabItem[] = PROJECT_SETTINGS_MENU_KEYS.map(key => ({
  key,
  labelId: `projects.settings.menu.${key}`,
  component: menuComponents[key],
  icon: getIconForKey(key),
  showInMobile: MOBILE_PROJECT_SETTINGS_MENU_KEYS.includes(key)
}));

const ProjectSettings: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { activeMenu } = useParams<{ activeMenu: string }>();
  const projectId = useCurrentProjectId<string>();
  const { projects, isLoading } = useCuratedProjects();
  const { isDesktop } = useIsMobile();

  const project = projects[projectId];
  const menu = find(allProjectMenuItems, { key: activeMenu as MenuKey });

  const menusToShow = useMemo(() => {
    if (isDesktop) return allProjectMenuItems;
    else return allProjectMenuItems.filter(menu => menu.showInMobile);
  }, [isDesktop]);

  if (isLoading) {
    return <Loader />;
  }

  if (!project) {
    return <ErrorContainer message="projects.settings.not.found" errorToReport="project not found" />;
  }

  if (!menu) {
    return <Navigate replace to={`/p/${projectId}/settings/general`} />;
  }

  const setActiveMenu = (menu: string) => navigate(`/p/${projectId}/settings/${menu}`);

  return (
    <VerticalTabsMenu
      items={menusToShow}
      activeKey={menu.key}
      onTabChange={setActiveMenu}
      componentProps={{ project }}
      dataE2e="project-settings-tabs"
    />
  );
};

export default observer(ProjectSettings);
